import React, { Suspense } from "react";
import PropTypes from "prop-types";
import { Flex, Box } from "orcs-design-system";
import { useLocation, withRouter } from "react-router-dom";
import styled, { css } from "styled-components";
import { themeGet } from "@styled-system/theme-get";

import useEmbedMode from "src/hooks/useEmbedMode";
import useEmbedHoverDetection from "src/hooks/useEmbedHoverDetection";
import useEmbedLinkInterceptor from "src/hooks/useEmbedLinkInterceptor";
import { useAppHeadersHeightCalc } from "src/hooks/useAppHeadersHeightCalc";
import { removeEmbedParams } from "src/util/embedUtils";
import Sidebar from "src/sidebar/Sidebar";
import Loading from "src/components/LoadingPage";
import { AINav } from "src/components/AINav";
import AppNavSimple from "../components/Nav/AppNavSimple";
import OutdatedBrowserMessage from "../components/OutdatedBrowserMessage";

const SidebarWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  height: 100%;
  min-height: 0;
`;

const Content = styled.section`
  overflow-y: auto !important;
  height: auto;
  padding: ${({ isEmbedMode }) => (isEmbedMode ? 0 : themeGet("space.r"))};
  ${({ isEmbedMode }) =>
    !isEmbedMode &&
    css`
      padding-top: 0;
      margin-top: ${themeGet("space.r")};
      overflow: hidden;
    `}
  background: transparent;
  display: flex;
  flex-direction: column;
  flex: 1;
  z-index: 2;
  ${({ aiViewMode }) =>
    aiViewMode === "fullscreen" &&
    css`
      display: none;
    `}
`;

const AppMain = styled.main`
  flex: 1;
  height: ${({ appHeadersHeightCalc }) =>
    appHeadersHeightCalc || "calc(100vh - 64px)"};
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  @media screen and (max-width: 900px) {
    ${({ isEmbedMode }) =>
      !isEmbedMode &&
      css`
        padding-bottom: 50px;
      `}
  }
`;

const TopNavContainer = styled(Box)`
  z-index: 10;
  box-shadow: 0 0px 10px rgba(0, 0, 0, 0.1);
`;

const EmbedTooltip = styled.div`
  position: fixed;
  top: ${({ mouseY, isVisible }) => {
    if (!isVisible || !mouseY) {
      return "-100px";
    }
    return `${mouseY - 60}px`;
  }};
  left: ${({ mouseX, isVisible }) => {
    if (!isVisible || !mouseX) {
      return "-100px";
    }
    return `${mouseX + 10}px`;
  }};
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  z-index: 9999;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transition: opacity 0.3s ease-in-out;
  pointer-events: none;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  max-width: 300px;
`;

const EmbedInteractionBlocker = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9998;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
  pointer-events: none;

  &:hover {
    opacity: 0.05;
  }
`;

const AppLayout = ({ emptyMode = false, children, themeSwitcher }) => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const aiViewMode = query.get("aiViewMode");
  const { isEmbedMode, hideHeader, hideSidebar } = useEmbedMode();
  const { hoveredUrl, isHovering, mousePosition } = useEmbedHoverDetection();
  const appHeadersHeightCalc = useAppHeadersHeightCalc({
    includeProfileHeaders: false,
    hideHeader,
  });

  // Initialize global link interceptor for embed mode
  useEmbedLinkInterceptor();

  const handleEmbedOverlayClick = (event) => {
    // Only handle clicks on the overlay itself, not on links
    if (event.target === event.currentTarget) {
      // Use hovered URL if available, otherwise fall back to current page
      const urlToOpen = hoveredUrl || window.location.href;
      const cleanUrl = removeEmbedParams(urlToOpen);
      // Open in new window/tab instead of same iframe
      window.open(cleanUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Flex
      flexDirection="column"
      height="100vh"
      overflow="hidden"
      background="#F2F4F7"
    >
      {!hideHeader && (
        <TopNavContainer id="app-layout-header">
          <OutdatedBrowserMessage />
          <AppNavSimple emptyMode={emptyMode} themeSwitcher={themeSwitcher} />
        </TopNavContainer>
      )}
      <AppMain
        appHeadersHeightCalc={appHeadersHeightCalc}
        isEmbedMode={isEmbedMode}
      >
        {!hideSidebar && (
          <SidebarWrapper>
            <Sidebar />
          </SidebarWrapper>
        )}

        <Content
          id="appContent"
          isEmbedMode={isEmbedMode}
          hideHeader={hideHeader}
          aiViewMode={aiViewMode}
        >
          <Suspense fallback={<Loading />}>{children}</Suspense>
        </Content>
        {!isEmbedMode && <AINav />}
      </AppMain>
      {isEmbedMode && (
        <>
          <EmbedInteractionBlocker
            onClick={handleEmbedOverlayClick}
            data-embed-overlay="true"
          />
          <EmbedTooltip
            isVisible={isHovering && hoveredUrl}
            mouseX={mousePosition.x}
            mouseY={mousePosition.y}
            data-embed-overlay="true"
          >
            {hoveredUrl ? "Open in TeamForm" : "Open in TeamForm"}
          </EmbedTooltip>
        </>
      )}
    </Flex>
  );
};

AppLayout.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  emptyMode: PropTypes.bool,
  themeSwitcher: PropTypes.func,
};

export default withRouter(AppLayout); // TODO: move to react hooks
